import { Injectable, EventEmitter } from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';

import { Observable } from 'rxjs';


import * as Objects from '@core/objects';
import * as Access from '@app/models/access';
import { VersionFeedback } from '@core/models/version-feedback';

import * as fromProjectsState from '@app/store/state/projects/projects.state';
import * as fromProjectsActions from '@app/store/state/projects/projects.actions';

@Injectable({
    providedIn: 'root'
})
export class ProjectsService {

    
    constructor( private store: Store<fromProjectsState.Projects>, private actions$: Actions,  protected http: HttpClient) {}

    load = (id: number) => this.store.dispatch( new fromProjectsActions.LoadProject(id));

    // this load only uploaded versions on store
    loadWithUploaded = (id: number) => this.store.dispatch( new fromProjectsActions.LoadWithUploadedVersions(id));

    clear = () => this.store.dispatch( new fromProjectsActions.Clear());
    // to use when leave versions section in project screen to avoid double load when come back
    clearRelatedVersions = () => this.store.dispatch( new fromProjectsActions.ClearRelatedVersions());

    update = (id: number, event: string) => this.store.dispatch( new fromProjectsActions.UpdateProject(id, event));

    updateMessages = (id: number) => this.store.dispatch( new fromProjectsActions.UpdateProjectMessages(id));

    // selectError = (key: string): Observable<HttpErrorResponse> => this.actions$.pipe(ofType(fromProjectsActions.ProjectsActionType.LoadProjectError));

    selectError = (key: string): Observable<HttpErrorResponse> => this.store.select(fromProjectsState.errorSelector(key));

    selectProject = (): Observable<Objects.Request> => this.store.select(fromProjectsState.projectSelector);
    
    selectProjectHeader = (): Observable<Objects.BasicRequest> => this.store.select(fromProjectsState.projectHeaderSelector);

    selectProjectFiles = (): Observable<Objects.File[]> => this.store.select(fromProjectsState.projectFilesSelector); 

    selectOriginVersion = (): Observable<Objects.Version> => this.store.select(fromProjectsState.projectOriginVersionSelector); 

    setActiveFile = (file: Objects.File, active: boolean) => this.store.dispatch( new fromProjectsActions.DeleteOneProjectFile(file, active));
    
    uploadFiles = (files: Objects.File[]) => this.store.dispatch( new fromProjectsActions.UploadProjectFiles(files));

    setAssign = (user: Objects.User) => this.store.dispatch( new fromProjectsActions.SetProjectAssigned(user));
    
    setDue = (date: string) => this.store.dispatch( new fromProjectsActions.SetProjectDue(date));

    setStatus = (status: Objects.RequestStatus) => this.store.dispatch( new fromProjectsActions.SetProjectStatus(status));

    setLabels = (labels: Array<Objects.Label>) => {
        if(labels.length == 0)
            return;
        this.store.dispatch( new fromProjectsActions.SetProjectLabels(labels));
    }

    loadMessages = () => this.store.dispatch( new fromProjectsActions.LoadProjectMessages());

    loadFilesNotesCounter = () => this.store.dispatch( new fromProjectsActions.LoadProjectFilesNotesCounter());

    selectProjectMessages = (): Observable<Objects.Message[]> => this.store.select(fromProjectsState.projectMessagesSelector);

    loadRelatedVersions = () => this.store.dispatch( new fromProjectsActions.LoadProjectRelatedVersions());
    
    loadUploadedVersions = () => this.store.dispatch( new fromProjectsActions.LoadProjectUploadedVersions() );

    selectRelatedVersions = (): Observable<Objects.RelatedVersions[]> => this.store.select(fromProjectsState.projectRelatedVersionsSelector);
    
    selectUploadedVersions = (): Observable<Objects.Version[]> => this.store.select(fromProjectsState.projectUploadedVersionsSelector);

    selectProjectFilesNotesCounter = (): Observable<Objects.AnnotationsCounter[]> => this.store.select(fromProjectsState.projectFilesNotesCounterSelector); 
    
    loadProjectEvents = () => this.store.dispatch( new fromProjectsActions.LoadProjectEvents());
    
    selectProjectEvents = (): Observable<Objects.LogbookEvent[]> => this.store.select(fromProjectsState.projectLogbookResultItemsSelector);

    setVersionStatus = (groupId:string,version: Objects.Version, status: Objects.Status) => this.store.dispatch( new fromProjectsActions.SetRelatedVersionStatus(groupId,version,status));

    setUploadedVersionStatus  = (version: Objects.Version, status: Objects.Status) => this.store.dispatch( new fromProjectsActions.SetUploadedVersionStatus(version,status));

    // feedbacks for artworks in project screens
    loadFeedbacks  = (versions: Objects.Version[]) => this.store.dispatch( new fromProjectsActions.LoadVersionsFeedbacks(versions));
  
    selectVersionsFeedbacks = (versions: Objects.Version[]) : Observable<VersionFeedback[]> => this.store.select(fromProjectsState.projectVersionsFeedbacksSelector(versions));

    // update from WS event
    updateFeedbacks  = (versionId: number) => this.store.dispatch( new fromProjectsActions.UpdateVersionsFeedbacks(versionId));

    updateRelatedVersion = (versionId: number) => this.store.dispatch( new fromProjectsActions.UpdateRelatedVersion(versionId));

    setInfo = (info: string) => this.store.dispatch(new fromProjectsActions.SetProjectInfo(info));
}

@Injectable({
    providedIn: 'root'
})
export class ProjectsEditorService {

    constructor(private store: Store<fromProjectsState.Projects>, private actions$: Actions) {}

    // loadAssigners = () => this.store.dispatch( new fromProjectsActions.LoadEditorAssigners());
    loadWorkflowTransitions = () => this.store.dispatch( new fromProjectsActions.LoadEditorWorkflowTransitions());

    loadSingleAccess = (id: number) => this.store.dispatch( new fromProjectsActions.LoadEditorSingleAccess(id));

    // used for clear single access in editor after dashboard calls
    clearAccess = () => this.store.dispatch( new fromProjectsActions.ClearEditorAccess());

    selectAssigners = (): Observable<Objects.User[]> => this.store.select(fromProjectsState.editorAssignersSelector);

    selectAccess = (key: string): Observable<boolean> => this.store.select(fromProjectsState.editorAccessSelector(key));

    selectAccessAll = () : Observable<Access.AccessProject> => this.store.select(fromProjectsState.editorAccessAllSelector);

    // deprecated sep-2022: maybe never used again
    // selectWorkflowTransitionsByOrigin = () : Observable<Objects.WorkflowTransition[]> => this.store.select(fromProjectsState.editorWorkflowTransitionsSelector);

    selectCurrentWorkflowTransitions = () : Observable<Objects.WorkflowTransition[]> => this.store.select(fromProjectsState.editorCurrentWorkflowTransitionsSelector);

    selectAllWorkflowTransitions = () : Observable<Objects.WorkflowTransition[]> => this.store.select(fromProjectsState.editorWorkflowTransitionsSelector);

    clearTransitions = () => this.store.dispatch( new fromProjectsActions.ClearEditorTransitions());

}
